import * as React from 'react'
import { cn } from '../../lib/utils'
import { CloseButton } from './close-button'
import { EntityGlyph, type EntityType } from './entity-glyph'
import { type BadgeColor } from './badge'

/**
 * EntityPreviewCard — an inline preview of a record that has no dedicated page
 * yet (a contact, a note, a document): the {@link EntityGlyph} + name + a short
 * subtitle, a divider, then a few detail rows and optional actions. Shown in a
 * popover from a pinned tile / recently-visited row, so a user can glance at the
 * record without leaving the homepage.
 *
 *   <EntityPreviewCard
 *     type="contact"
 *     name="Daniel Paulsen"
 *     subtitle="Chief Executive Officer · La Carreta"
 *     onClose={close}
 *   >
 *     <EntityPreviewRow icon={<Envelope />}>danipaulsen@gmail.com</EntityPreviewRow>
 *   </EntityPreviewCard>
 *
 * When a record *does* get its own page (contacts, eventually), pass a "View
 * page" action alongside the preview so both are available.
 */

export interface EntityPreviewCardProps {
  /** Record type — resolves the header glyph. */
  type?: EntityType
  /** Glyph icon override. */
  icon?: React.ReactNode
  /** Glyph tint override. */
  color?: BadgeColor
  /** Record name (header title). */
  name: React.ReactNode
  /** Secondary header line — a role · company, a file type · size. */
  subtitle?: React.ReactNode
  /** Shows a close × in the header corner. */
  onClose?: () => void
  /** Let the title wrap instead of truncating — for long names like filenames. */
  wrapTitle?: boolean
  /**
   * Hide the leading glyph. Use when the card opens from a surface that already
   * shows the record's glyph (e.g. a pinned tile) — repeating it there is a
   * redundant visual echo; the name + subtitle already identify the record.
   */
  hideGlyph?: boolean
  /** Detail rows — compose {@link EntityPreviewRow}s. */
  children?: React.ReactNode
  /** Footer actions (a "View page" link, a Download button). */
  actions?: React.ReactNode
  className?: string
}

export const EntityPreviewCard = React.forwardRef<
  HTMLDivElement,
  EntityPreviewCardProps
>(
  (
    {
      type,
      icon,
      color,
      name,
      subtitle,
      onClose,
      wrapTitle = false,
      hideGlyph = false,
      children,
      actions,
      className,
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cn(
        'w-80 rounded-xl border border-fbui-gray-200 bg-fbui-base-white p-[1rem] shadow-fbui-lg',
        className
      )}
    >
      <div className="flex items-start gap-[0.75rem]">
        {!hideGlyph && <EntityGlyph type={type} icon={icon} color={color} />}
        <div className="flex min-w-0 flex-1 flex-col">
          <p
            className={cn(
              'text-base font-semibold text-fbui-gray-900',
              wrapTitle ? 'break-words' : 'truncate'
            )}
          >
            {name}
          </p>
          {subtitle != null && (
            <p className="text-sm text-fbui-gray-500">{subtitle}</p>
          )}
        </div>
        {onClose && (
          <CloseButton onClick={onClose} aria-label="Close preview" className="-mr-1 -mt-1" />
        )}
      </div>

      {(children != null || actions != null) && (
        <div className="mt-[0.75rem] border-t border-fbui-gray-100 pt-[0.75rem]">
          {children != null && <div className="flex flex-col gap-2">{children}</div>}
          {actions != null && (
            <div className="mt-[0.75rem] flex items-center gap-[1rem]">{actions}</div>
          )}
        </div>
      )}
    </div>
  )
)
EntityPreviewCard.displayName = 'EntityPreviewCard'

export interface EntityPreviewRowProps {
  /** Leading icon (a muted gray glyph). */
  icon?: React.ReactNode
  children: React.ReactNode
  className?: string
}

/** One detail row inside an {@link EntityPreviewCard}: a muted icon + text. */
export const EntityPreviewRow = ({ icon, children, className }: EntityPreviewRowProps) => (
  <div className={cn('flex items-center gap-2 text-sm text-fbui-gray-700', className)}>
    {icon != null && (
      <span className="shrink-0 text-fbui-gray-400 [&>svg]:size-4">{icon}</span>
    )}
    <span className="min-w-0 flex-1 truncate">{children}</span>
  </div>
)
EntityPreviewRow.displayName = 'EntityPreviewRow'
